import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { CheckIssue } from '@tabservice/tabfile-client';
import { ErrorNote } from '@/components/ui/feedback';
import { filingQuery } from '@/lib/queries';
import { formatDate } from '@/lib/format';

/**
 * Tab Service keeps a hash of every file it has filed, so the same file sent twice
 * is caught before it reaches the IRS. The e-file it matches says when.
 */
export function DuplicateFileNote({ filingId }: { filingId: string }) {
  const filing = useQuery({ ...filingQuery(filingId), staleTime: 60_000 });
  const filedOn = filing.data ? formatDate(filing.data.createdAt) : null;

  return (
    <ErrorNote
      title="This file has been filed before"
      detail={
        filedOn
          ? `This exact file was already filed on ${filedOn}. Filing it again would send the IRS duplicate returns.`
          : 'This exact file was already filed. Filing it again would send the IRS duplicate returns.'
      }
      action={
        <Link to="/e-files/$efileId" params={{ efileId: filingId }} className="text-base font-medium text-link hover:underline">
          Open the e-file it matches
        </Link>
      }
    />
  );
}

/** The DUPLICATE_RECORDS issues out of a check result, if there are any. */
export function duplicateRecordIssues(issues: CheckIssue[]): CheckIssue[] {
  return issues.filter((issue) => issue.code === 'DUPLICATE_RECORDS');
}

/**
 * The API names the e-file that already holds these records in the issue
 * message. Tab Service links to it when it does, because the way out of a duplicate
 * is a correction, not another submission.
 */
function filingIdIn(issues: CheckIssue[]): string | null {
  for (const issue of issues) {
    const match = /\b(fil_[A-Za-z0-9_]+)\b/.exec(issue.message);
    if (match?.[1]) return match[1];
  }
  return null;
}

export function DuplicateRecordsNote({ issues }: { issues: CheckIssue[] }) {
  const duplicates = duplicateRecordIssues(issues);
  if (duplicates.length === 0) return null;

  const refs = duplicates.map((issue) => issue.recordRef).filter((ref): ref is string => Boolean(ref));
  const shown = refs.slice(0, 6).join(', ');
  const filingId = filingIdIn(duplicates);

  return (
    <ErrorNote
      title={`${refs.length || duplicates.length} of these records were filed before`}
      detail={
        refs.length > 0
          ? `Already filed: ${shown}${refs.length > 6 ? ` and ${refs.length - 6} more` : ''}. Filing them again would send the IRS duplicate returns. Correct the accepted records instead.`
          : 'These records were filed before. Filing them again would send the IRS duplicate returns. Correct the accepted records instead.'
      }
      action={
        filingId ? (
          <Link
            to="/e-files/$efileId/correct"
            params={{ efileId: filingId }}
            className="text-base font-medium text-link hover:underline"
          >
            Submit corrections instead
          </Link>
        ) : (
          <Link to="/e-files" className="text-base font-medium text-link hover:underline">
            Find the e-file that holds them
          </Link>
        )
      }
    />
  );
}
